import {
  NavigationProp,
  useFocusEffect,
  useNavigation,
} from "@react-navigation/native";
import { useCallback, useMemo, useState } from "react";
import { useWindowDimensions } from "react-native";

import { QuaggaJSResultObject } from "@ericblade/quagga2";
import { MaterialIcons } from "@expo/vector-icons";
import { useFlashMessages } from "@towersystems/roam-common-native";
import { CameraViewFinder } from "../../../../../../components";
import { BarcodeScanner } from "../../../../../../components/BarcodeScanner/BarcodeScanner.web";
import {
  computeScannableArea,
  toOverlayPaddingPercentage,
} from "../../../../../../utilities/barcode-scanner/utils";
import { isStockNumberNotFoundError } from "../../../../../../utilities/product";
import { useProductIdByStockNumberQuery } from "../../../../../../utilities/product/hooks";
import { BarcodeScannerOverlay } from "../../../../components";

export const BarcodeScannerScreen = () => {
  const { showMessage } = useFlashMessages();

  const { width: screenWidth, height: screenHeight } = useWindowDimensions();

  const navigation = useNavigation<NavigationProp<any>>();

  const { navigate } = navigation;

  // used to stop scanning until screen regains focus
  const [isActive, setIsActive] = useState(false);

  const { getProductIdByStockNumber } = useProductIdByStockNumberQuery();

  const handleCancel = useCallback(() => {
    (navigation as any).pop();
  }, []);

  const handleScanned = ({ codeResult }: QuaggaJSResultObject) => {
    setIsActive(false);

    const stockNumber = codeResult.code;

    if (!stockNumber) {
      setIsActive(true);
      return;
    }

    getProductIdByStockNumber(stockNumber)
      .then((res) => {
        const format = codeResult.format;

        if (isStockNumberNotFoundError(res)) {
          navigate("RetailerOnlyScannedPartialScreen", {
            sku: stockNumber,
            format,
          });
        } else {
          navigate("ProductScannedPartialScreen", {
            productId: res.productId,
            format,
          });
        }
      })
      .catch((e) => {
        showMessage({
          message: "Unexpected error occurred",
          severity: "error",
        });
        setIsActive(true);
      });
  };

  function handleSettingsPress() {
    navigate("BarcodeSettingsScreen");
  }

  useFocusEffect(
    useCallback(() => {
      setIsActive(true);

      return () => {
        setIsActive(false);
      };
    }, [])
  );

  const scannableArea = useMemo(
    () =>
      computeScannableArea({
        screenWidth,
        screenHeight,
      }),
    [screenWidth, screenHeight]
  );

  const decodeArea = useMemo(
    () => toOverlayPaddingPercentage(scannableArea, screenWidth, screenHeight),
    [scannableArea]
  );

  return (
    <BarcodeScanner
      active={isActive}
      scannableArea={scannableArea}
      headerProps={{
        showBackButton: false,
        rightComponent: (
          <CameraViewFinder.IconButton onPress={handleSettingsPress}>
            <MaterialIcons name="settings" size={24} color="white" />
          </CameraViewFinder.IconButton>
        ),
      }}
      overlayElement={
        <BarcodeScannerOverlay
          horizontalPadding={decodeArea.left}
          verticalPadding={decodeArea.top}
          onCancel={handleCancel}
        />
      }
      onBarCodeScanned={handleScanned}
    />
  );
};
